(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else { root.EdiBotLaunchGift = api; api.initialize(); }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';
  const campaign = 'edibot-first-100';
  const includedGifts = '1-Year Ink Care + Large-format Positioning Ruler — 1 gift pack per set';
  const migrationError = 'Your previous gift selection needs review. Please open your cart or contact us before checkout. No gift entitlement has been removed.';
  function validStatus(value) {
    return Boolean(value && value.campaign === campaign && Number.isInteger(value.remaining) && value.remaining >= 0 && value.remaining <= 100 && value.total === 100 && Array.isArray(value.eligibleVariantIds));
  }
  function allocation(cart, status) {
    if (!validStatus(status)) throw new Error('Could not verify launch gift availability. Please refresh and try again.');
    const eligible = new Set(status.eligibleVariantIds.map(Number));
    const quantity = cart.items.reduce((sum, item) => sum + (eligible.has(Number(item.variant_id)) ? item.quantity : 0), 0);
    return status.enabled && status.available ? Math.min(quantity, status.remaining) : 0;
  }
  function giftLines(cart, status) { return cart.items.filter(item => Number(item.variant_id) === Number(status.variantId)); }
  function changes(cart, status) {
    const desired = allocation(cart, status), lines = giftLines(cart, status), updates = {};
    lines.forEach((line, index) => { const next = index === 0 ? desired : 0; if (line.quantity !== next) updates[line.key] = next; });
    return { desired, updates, add: lines.length ? 0 : desired };
  }
  function free(cart, status) { return giftLines(cart, status).every(item => item.final_line_price === 0); }
  let settings = null, latest = null, queue = Promise.resolve(), initialized = false, pendingSyncs = 0, legacyMigration = null;
  const includedMode = () => settings?.fulfillmentMode === 'included';
  const needsLegacyMigration = () => Boolean(includedMode() && settings.legacyGiftPresent);
  const prefix = () => typeof window !== 'undefined' && window.Shopify?.routes?.root || '/';
  async function request(path, data, headers = { 'Content-Type': 'application/json', Accept: 'application/json' }) {
    const controller = new AbortController();
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        reject(new Error('The gift check is taking too long.'));
        controller.abort();
      }, 12000);
    });
    try {
      // Bound both the response and its body, even when an app's fetch wrapper ignores abort.
      return await Promise.race([timeout, (async () => {
        const response = await fetch(path, { signal: controller.signal, method: data ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers, ...(data ? { body: JSON.stringify(data) } : {}) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Could not verify or update launch gifts. Please try again.');
        return result;
      })()]);
    } finally { clearTimeout(timer); }
  }
  function emitStatus(value) {
    latest = value;
    document.dispatchEvent(new CustomEvent('edibot:gift-status', { detail: value }));
  }
  async function refresh() {
    if (!settings?.statusUrl) throw new Error('Launch gifts are not connected.');
    try {
      // This is a Liquid product view; Accept: application/json selects Shopify's product JSON instead.
      const status = await request(settings.statusUrl + '&_=' + Date.now(), undefined, {});
      if (!validStatus(status) || status.variantId !== settings.variantId) throw new Error('Could not verify gift availability.');
      const next = includedMode() ? { ...settings, ...status, fulfillmentMode: 'included', legacyGiftPresent: settings.legacyGiftPresent, stale: false } : status;
      emitStatus(next); return next;
    } catch (error) {
      // A progress refresh must not turn an already displayed included gift into
      // a paid add-on. Keep its last known value and expose that it is stale.
      emitStatus(includedMode() && latest ? { ...latest, stale: true } : null);
      throw error;
    }
  }
  function legacyMigrationPlan(cart, status) {
    if (!Array.isArray(cart?.items)) throw new Error(migrationError);
    const gifts = giftLines(cart, status);
    if (!gifts.length) return { properties: [], remove: {} };
    // This SKU can only be removed automatically when the old gift module
    // explicitly marked it as promotional, never when purchased independently.
    if (gifts.some(item => item.properties?._edibot_campaign !== campaign)) throw new Error(migrationError);
    const eligible = new Set(status.eligibleVariantIds.map(Number));
    const sets = cart.items.filter(item => eligible.has(Number(item.variant_id)));
    const pending = sets.filter(item => !item.properties?.['Included gifts']);
    const migrated = sets.filter(item => item.properties?._edibot_gift_migration === campaign && item.properties?.['Included gifts'] === includedGifts);
    const quantity = items => items.reduce((sum, item) => sum + item.quantity, 0);
    const giftQuantity = quantity(gifts);
    const representedQuantity = quantity(pending) + quantity(migrated);
    const alreadyIncluded = sets.filter(item => item.properties?.['Included gifts'] === includedGifts);
    const redundant = !pending.length && quantity(alreadyIncluded) >= giftQuantity;
    // Partial allocations cannot be safely described as one gift per set.
    // Leave them intact for review rather than inventing additional gifts.
    if (sets.length && representedQuantity !== giftQuantity && !redundant) throw new Error(migrationError);
    return {
      properties: pending.map(item => ({ id: item.key, quantity: item.quantity, properties: { ...item.properties, 'Included gifts': includedGifts, '_edibot_gift_migration': campaign } })),
      remove: Object.fromEntries(gifts.map(item => [item.key, 0])),
    };
  }
  function migrateLegacyCart(cart, options = {}) {
    if (legacyMigration) return legacyMigration;
    if (!needsLegacyMigration()) return Promise.resolve(cart);
    legacyMigration = (async () => {
      let next = await request(prefix() + 'cart.js');
      let plan = legacyMigrationPlan(next, settings);
      // Recompute after every change: Shopify can change line keys or merge
      // equivalent lines when their properties change. Existing properties stay.
      while (plan.properties.length) {
        const pendingCount = plan.properties.length;
        next = await request(prefix() + 'cart/change.js', plan.properties[0]);
        plan = legacyMigrationPlan(next, settings);
        if (plan.properties.length >= pendingCount) throw new Error(migrationError);
      }
      if (Object.keys(plan.remove).length) {
        const sections = Array.isArray(options.sections) && options.sections.length ? options.sections : undefined;
        next = await request(prefix() + 'cart/update.js', { updates: plan.remove, ...(sections ? { sections } : {}) });
        if (giftLines(next, settings).length) throw new Error(migrationError);
      }
      settings.legacyGiftPresent = false;
      if (latest) latest = { ...latest, legacyGiftPresent: false };
      return next;
    })().finally(() => { legacyMigration = null; });
    return legacyMigration;
  }
  function sync(_cart, options = {}) {
    if (includedMode()) {
      if (Array.isArray(_cart?.items) && giftLines(_cart, settings).length) settings.legacyGiftPresent = true;
      return migrateLegacyCart(_cart, options);
    }
    // A preceding sync can change gifts after the caller rendered its sections.
    const canReuseSections = pendingSyncs === 0;
    pendingSyncs++;
    const work = queue.then(async () => {
      if (!settings) return _cart;
      // Known eligible purchases can read both fresh snapshots together. Both
      // reads must succeed before a gift mutation is allowed to start.
      let cart, status;
      if (options.parallelStatus) {
        [cart, status] = await Promise.all([request(prefix() + 'cart.js'), refresh()]);
      } else {
        cart = await request(prefix() + 'cart.js');
      }
      const sections = Array.isArray(options.sections) && options.sections.length ? options.sections : undefined;
      // Add responses can contain only the newly added items. Always retain the
      // fresh full cart; only its unchanged section HTML can come from that add.
      const unchangedCart = () => canReuseSections && sections && _cart && Object.prototype.hasOwnProperty.call(_cart, 'sections')
        ? { ...cart, sections: _cart.sections }
        : cart;
      const relevant = cart.items.some(item => settings.eligibleVariantIds.includes(Number(item.variant_id)) || Number(item.variant_id) === settings.variantId);
      if (!relevant) return unchangedCart();
      if (!status) status = await refresh();
      const change = changes(cart, status);
      let next = unchangedCart();
      if (Object.keys(change.updates).length) next = await request(prefix() + 'cart/update.js', { updates: change.updates, ...(sections ? { sections } : {}) });
      if (change.add) {
        await request(prefix() + 'cart/add.js', { items: [{ id: status.variantId, quantity: change.add, properties: { 'Launch gifts': '1-Year Ink Care + Large-format Positioning Ruler', '_edibot_campaign': campaign } }] });
        // One final response supplies both the complete discounted cart and the
        // matching drawer HTML. Buy now only needs the cart JSON.
        next = sections
          ? await request(prefix() + 'cart/update.js', { updates: {}, sections })
          : await request(prefix() + 'cart.js');
      }
      if (!free(next, status)) {
        const remove = Object.fromEntries(giftLines(next, status).map(item => [item.key, 0]));
        await request(prefix() + 'cart/update.js', { updates: remove });
        throw new Error('The free launch gifts could not be applied. Remove any conflicting discount and try again. No paid gift pack was kept in your cart.');
      }
      // Product purchases have a guarded drawer GET fallback. Existing cart
      // callers still need one full cart/section response when none was supplied.
      if (sections && !options.deferSectionFallback && !next.sections) {
        next = await request(prefix() + 'cart/update.js', { updates: {}, sections });
      }
      return next;
    }).finally(() => { pendingSyncs--; });
    queue = work.catch(() => {});
    return work;
  }
  function showError(message) {
    let note = document.querySelector('[data-edibot-gift-cart-error]');
    if (!note) {
      note = document.createElement('p'); note.setAttribute('data-edibot-gift-cart-error', ''); note.setAttribute('role', 'alert');
      note.style.cssText = 'padding:12px 16px;color:#a52e27;background:#fff3ef;font-size:14px;line-height:1.5';
      const checkout = document.querySelector('[name="checkout"]');
      (checkout?.parentElement || document.body).prepend(note);
    }
    note.textContent = message;
  }
  function initialize() {
    if (initialized) return;
    const node = document.querySelector('[data-edibot-gift-config]');
    if (!node) return;
    try { settings = JSON.parse(node.textContent); } catch (_) { return; }
    if (!validStatus(settings) || !settings.variantId) { settings = null; return; }
    initialized = true; latest = settings;
    // Revalidate product-page availability; never reserve stock from a page view.
    if (document.querySelector('[data-edp-config]')) {
      refresh().catch(() => {});
      window.setInterval(() => { if (!document.hidden) refresh().catch(() => {}); }, 60000);
    }
    document.addEventListener('visibilitychange', () => { if (!document.hidden && document.querySelector('[data-edp-config]')) refresh().catch(() => {}); });
    // Fresh included-gift carts require no cart reads, writes or checkout
    // interception. Only a legacy promotional SKU needs this one-time cleanup.
    if (includedMode()) {
      const sections = [...new Set([...(document.querySelectorAll?.('cart-items[data-section-id]') || [])].map(node => node.dataset.sectionId))];
      const updateCart = cart => {
        if (cart && window.theme?.pubsub) window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'edibot-gift-migration', cart });
      };
      document.addEventListener('submit', async event => {
        if (!needsLegacyMigration() || !event.submitter?.matches('[name="checkout"]')) return;
        event.preventDefault(); event.stopImmediatePropagation();
        const button = event.submitter; button.disabled = true;
        try { updateCart(await migrateLegacyCart(undefined, { sections })); window.location.assign(prefix() + 'checkout'); }
        catch (error) { showError(error.message); button.disabled = false; }
      }, true);
      if (needsLegacyMigration()) migrateLegacyCart(undefined, { sections }).then(updateCart).catch(error => showError(error.message));
      return;
    }
    document.addEventListener('submit', async event => {
      if (!event.submitter?.matches('[name="checkout"]')) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const button = event.submitter; button.disabled = true;
      try { await sync(); window.location.assign(prefix() + 'checkout'); }
      catch (error) { showError(error.message); button.disabled = false; }
    }, true);
    if (window.location.pathname.endsWith('/cart')) {
      const sections = [...new Set([...document.querySelectorAll('cart-items[data-section-id]')].map(node => node.dataset.sectionId))];
      sync(undefined, { sections }).then(cart => {
        if (cart && window.theme?.pubsub) window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'edibot-gift', cart });
      }).catch(error => showError(error.message));
    }
  }
  return { validStatus, allocation, changes, free, refresh, sync, initialize, legacyMigrationPlan, migrateLegacyCart, needsLegacyMigration, getStatus: () => latest, showError };
});
